import { supabase } from "@/lib/supabase";
import { API_ENDPOINTS } from "../constants/api";
import { TOKEN_API } from "../constants/enviroment";

export interface PendingInvitation {
  id: number;
  account_id: string;
  account_name: string | null;
  email: string;
  token: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
  inviter_email: string | null;
}

export interface InvitationAcceptInfo {
  id: number;
  account_id: string;
  account_name: string | null;
  email: string;
  role: string;
  status: string;
  expires_at: string;
  created_at: string;
  inviter_email: string | null;
}

export interface AcceptInvitationResult {
  success: boolean;
  account_id: string;
  member_id: number;
  role: string;
}

const SESSION_ERROR_MESSAGE = "Session expired or invalid. Please sign in again.";

async function getAuthParams(): Promise<
  { userId: string; authHeader: string } | { error: string }
> {
  const { data } = await supabase.auth.getSession();
  const userId = data.session?.user?.id;
  if (!userId) return { error: SESSION_ERROR_MESSAGE };
  if (!TOKEN_API) return { error: "API key is not configured (VITE_TOKEN_API)." };
  return { userId, authHeader: `Bearer ${TOKEN_API}` };
}

async function extractErrorOrJson<T>(
  res: Response,
  fallbackMessage: string
): Promise<T | { error: string }> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail =
      (body as { detail?: string })?.detail ?? fallbackMessage;
    return { error: detail };
  }
  return res.json();
}

export async function getPendingInvitations(): Promise<
  PendingInvitation[] | { error: string }
> {
  const params = await getAuthParams();
  if ("error" in params) return params;
  const { userId, authHeader } = params;
  const url = `${API_ENDPOINTS.invitations}/pending?user_id=${encodeURIComponent(userId)}`;
  const res = await fetch(url, {
    headers: { Authorization: authHeader },
  });
  return extractErrorOrJson<PendingInvitation[]>(res, "Failed to load invitations.");
}

export async function getInvitationByToken(
  token: string
): Promise<InvitationAcceptInfo | { error: string }> {
  const res = await fetch(
    `${API_ENDPOINTS.invitations}/accept/${encodeURIComponent(token)}`
  );
  return extractErrorOrJson<InvitationAcceptInfo>(res, "Invitation not found or expired.");
}

export async function acceptInvitation(
  token: string
): Promise<AcceptInvitationResult | { error: string }> {
  const params = await getAuthParams();
  if ("error" in params) return params;
  const { userId, authHeader } = params;
  const url = `${API_ENDPOINTS.invitations}/accept/${encodeURIComponent(token)}?user_id=${encodeURIComponent(userId)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: authHeader },
  });
  return extractErrorOrJson<AcceptInvitationResult>(res, "Failed to accept invitation.");
}

export interface RegisterByInvitationData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

export async function registerByInvitation(
  token: string,
  data: RegisterByInvitationData
): Promise<AcceptInvitationResult | { error: string }> {
  const url = `${API_ENDPOINTS.invitations}/register/${encodeURIComponent(token)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return extractErrorOrJson<AcceptInvitationResult>(res, "Failed to register.");
}

export interface AcceptAgencyInvitationResult {
  success: boolean;
  managed_account_id: number;
}

export async function acceptAgencyInvitation(
  token: string
): Promise<AcceptAgencyInvitationResult | { error: string }> {
  const url = `${API_ENDPOINTS.agencyAcceptInvite}?token=${encodeURIComponent(token)}`;
  const res = await fetch(url, { method: "POST" });
  return extractErrorOrJson<AcceptAgencyInvitationResult>(res, "Failed to accept agency invitation.");
}
